import { useState, useEffect } from "react"
import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Center,
  Loader,
  Image,
  Box,
  TextInput,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { useTwoFactorSetup, useVerifyTwoFactorSetup } from "../hooks/useTwoFactorSetup"

export interface TwoFactorDialogProps {
  opened: boolean
  onClose: () => void
}

export function TwoFactorDialog({ opened, onClose }: TwoFactorDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: setupData, isLoading, error } = useTwoFactorSetup()
  const verifyMutation = useVerifyTwoFactorSetup()

  const [code, setCode] = useState("")
  const [localError, setLocalError] = useState<string | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)
  const [manualOpen, { toggle: toggleManual }] = useDisclosure(false)

  const displayEmail = setupData?.email || setupData?.username || ""

  useEffect(() => {
    if (verifyMutation.isSuccess) {
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["user"] })
        onClose()
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [verifyMutation.isSuccess, onClose, queryClient])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLocalError(null)

    const trimmed = code.replace(/\s/g, "")
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      setLocalError(t("user.twoFactor.codeRequired", { defaultValue: "Enter a valid 6-digit code" }))
      return
    }

    if (!setupData?.secret) {
      setLocalError(t("user.twoFactor.sessionExpired", { defaultValue: "Session expired. Please try again." }))
      return
    }

    try {
      await verifyMutation.mutateAsync({ code: trimmed, secret: setupData.secret })
    } catch {
      // handled by mutation state
    }
  }

  const copySecret = () => {
    if (!setupData?.secret) return
    navigator.clipboard.writeText(setupData.secret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 1500)
  }

  const displayError = localError || (verifyMutation.isError ? (verifyMutation.error as Error).message : null)

  if (error) {
    return (
      <Modal opened={opened} onClose={onClose} centered size="sm" withCloseButton={false}>
        <Stack gap="md" align="center" py="xl">
          <i className="fa fa-exclamation-circle" style={{ fontSize: 48, color: "#ef4444" }} />
          <Text size="lg" fw={600} c="red">
            {t("user.twoFactor.errorTitle", { defaultValue: "Error" })}
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {error?.message || t("user.twoFactor.errorLoading", { defaultValue: "Failed to load setup data." })}
          </Text>
          <Button variant="default" onClick={onClose}>
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </Stack>
      </Modal>
    )
  }

  if (isLoading || !setupData) {
    return (
      <Modal opened={opened} onClose={onClose} centered size="sm" withCloseButton={false}>
        <Center py="xl">
          <Loader />
        </Center>
      </Modal>
    )
  }

  if (verifyMutation.isSuccess) {
    return (
      <Modal opened={opened} onClose={onClose} centered size="sm" withCloseButton={false}>
        <Stack gap="md" align="center" py="xl">
          <i className="fa fa-check-circle" style={{ fontSize: 48, color: "#22c55e" }} />
          <Text size="lg" fw={600} ta="center">
            {t("user.twoFactor.successTitle", { defaultValue: "Authenticator configured!" })}
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            {t("user.twoFactor.successMessage", { defaultValue: "Your account is now more secure." })}
          </Text>
        </Stack>
      </Modal>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="xl"
      withCloseButton
      overlayProps={{ blur: 3, opacity: 0.5 }}
    >
      <Stack gap={0} p="xl">
        <Text size="xl" fw={700} ta="center" mb="lg">
          {t("user.twoFactor.setupTitle", { defaultValue: "Configurar Autenticador" })}
        </Text>

        <Group gap="xl" align="flex-start">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" mb="md" ta="center">
              1. {t("user.twoFactor.scanTheQRCode", { defaultValue: "Digitalize o código QR" })}
            </Text>

            <Stack align="center" gap="sm">
              <Box style={{ border: "1px solid #ddd", padding: 8, background: "#fff" }}>
                <Image
                  src={setupData.qr_code_url.replace("200x200", "160x160")}
                  alt={t("user.twoFactor.qrAlt", { defaultValue: "QR code" })}
                  radius="md"
                />
              </Box>

              <Button
                variant="subtle"
                size="compact-xs"
                onClick={toggleManual}
                aria-label={t("user.twoFactor.orEnterManually", { defaultValue: "Ou insira manualmente" })}
                rightSection={<i className={`fa fa-caret-${manualOpen ? "up" : "down"}`} />}
                px={0}
              >
                {t("user.twoFactor.orEnterManually", { defaultValue: "Ou insira manualmente" })}
              </Button>

              {manualOpen && (
                <Box
                  style={{
                    width: "100%",
                    padding: 12,
                    background: "#f9f9f9",
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  <Group justify="space-between" mb={6}>
                    <Text size="sm">
                      <Text span fw={600}>{t("user.twoFactor.account", { defaultValue: "Conta:" })}</Text>{" "}
                      <Text span c="dimmed">{displayEmail}</Text>
                    </Text>
                  </Group>
                  <Group justify="space-between" mb={0}>
                    <Text size="sm" style={{ flex: 1 }}>
                      <Text span fw={600}>{t("user.twoFactor.key", { defaultValue: "Chave:" })}</Text>{" "}
                      <Text span ff="monospace" style={{ wordBreak: "break-all" }}>{setupData?.secret}</Text>
                    </Text>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={copySecret}
                    >
                      {copiedSecret ? <i className="fa fa-check" style={{ fontSize: 12 }} /> : <i className="fa fa-copy" style={{ fontSize: 12 }} />}
                    </Button>
                  </Group>
                </Box>
              )}

              <Text size="xs" c="dimmed" ta="center" mt="sm">
                {t("user.twoFactor.installAuthenticatorApp", {
                  defaultValue: "Instale uma aplicação autenticadora no seu telemóvel:",
                })}{" "}
                <br />
                {t("user.twoFactor.authenticatorApps", {
                  defaultValue: "Google Authenticator, Microsoft Authenticator, Authy",
                })}
              </Text>
            </Stack>
          </Box>

          <Box style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Text fw={600} size="sm" mb="md" ta="center">
              2. {t("user.twoFactor.enterCode", { defaultValue: "Insira o código de 6 dígitos" })}
            </Text>

            <form onSubmit={handleSubmit}>
              <Stack gap="sm" align="center">
                <TextInput
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(event) => setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus
                  styles={{
                    input: { textAlign: "center", letterSpacing: 8, fontSize: 20 },
                  }}
                  aria-label={t("user.twoFactor.codeInput", { defaultValue: "6-digit code" })}
                />
                {displayError && (
                  <Text size="xs" c="red" ta="center">
                    {displayError}
                  </Text>
                )}
                <Button
                  type="submit"
                  color="green"
                  loading={verifyMutation.isPending}
                  disabled={code.length !== 6}
                  fullWidth
                >
                  {t("user.twoFactor.confirm", { defaultValue: "Confirmar e Ativar" })}
                </Button>
              </Stack>
            </form>
          </Box>
        </Group>
      </Stack>
    </Modal>
  )
}

export default function TwoFactorSetupPage() {
  const [opened, { open, close }] = useDisclosure(false)
  return <TwoFactorDialog opened={opened} onClose={close} />
}